import { Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FC, useCallback, useMemo, useRef } from 'react';

import { TLocalesKeys } from '../../../../types';
import { hasContent } from '../../../../util/guards';
import { IDomainItem, IDomainSelector } from '../../../common/messageCreators';
import { useScrollIntoView, useUserActionClickSelector } from '../../hooks';
import { MessageItem } from '../MessageItem';
import { SelectorFooter, SelectorItem, SelectorWrapper } from '../Selectors';
import { usePagination } from '../Selectors/usePagination';

export const DomainSelector: FC<IDomainSelector> = ({ id, type, items, pageSize }) => {
  const emit = useUserActionClickSelector();
  const { t } = useTranslation();
  const { hasMore, visibleItems, loadMore, focusIndex } = usePagination(items, pageSize);
  const ref = useRef<HTMLDivElement>(null);
  const scrollIntoView = useScrollIntoView(ref);

  const footerLabel = t<TLocalesKeys>(hasMore ? 'selector_view_more' : 'select_domain_finish');

  const onClick = useCallback(
    (item: IDomainItem, position: number) => {
      emit(type, {
        label: item.title,
        position,
        item,
      });
    },
    [emit, type],
  );

  const onClickFooter = useCallback(() => {
    if (hasMore) {
      loadMore();
      scrollIntoView();
    } else {
      emit(type, {
        label: footerLabel,
        footer: true,
      });
    }
  }, [emit, type, footerLabel, hasMore, loadMore, scrollIntoView]);

  const itemsJsx = useMemo(
    () =>
      visibleItems.map((item, i) => (
        <SelectorItem key={item._id} onClick={() => onClick(item, i)} hasFocus={i === focusIndex}>
          <Text dataHook="title" ellipsis>
            {item.title}
          </Text>
          <Text dataHook="subtitle" ellipsis size="small" secondary light>
            {hasContent(item.siteName) ? item.siteName : t<TLocalesKeys>('select_domain_unassigned')}
          </Text>
        </SelectorItem>
      )),
    [focusIndex, onClick, t, visibleItems],
  );

  return (
    <MessageItem id={id} ref={ref}>
      <SelectorWrapper>
        {itemsJsx}
        <SelectorFooter label={footerLabel} onClick={onClickFooter} />
      </SelectorWrapper>
    </MessageItem>
  );
};
